import axios, {
  type InternalAxiosRequestConfig,
  type AxiosRequestConfig,
} from "axios";
import { handleResponseError } from "./serverErrorHandler";
import { Config, apiServiceHeadersConfig } from "../constants/config";
import { KeyExchangeApi, refreshAccessToken } from "./Apiservices";

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _retryCount?: number;
}

let isRefreshing = false;

let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });

  failedQueue = [];
};

const getBaseUrl = () => {
  const origin = window.location.origin;

  if (origin.includes("localhost:9000")) {
    return Config.baseUrls.localhost;
  }

  if (origin.includes("digibank")) {
    return origin.replace("digibank", "softtoken");
  }
  return origin;
};

const axiosInstance = axios.create({
  headers: {
    "Content-Type": "application/json",
    [apiServiceHeadersConfig.channelType]: "WEB",
  },
  baseURL: getBaseUrl(),
});

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: any) => {
    const originalRequest = error.config as CustomAxiosRequestConfig;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (!isRefreshing) {
        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newAuthToken = await refreshAccessToken();
          isRefreshing = false;
          processQueue(null, newAuthToken);
          originalRequest.headers.Authorization = newAuthToken;
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;

          processQueue(refreshError, null);
          return Promise.reject(refreshError);
        }
      } else {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            originalRequest.headers[apiServiceHeadersConfig.secureSessionId] = undefined;
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }
    }

    if (error.response?.status === 419) {
      originalRequest._retryCount = originalRequest._retryCount || 0;
      if (originalRequest._retryCount < 1) {
        originalRequest._retryCount += 1;
        try {
          // Call the keyExchange endpoint
          let res = await KeyExchangeApi();
          if (typeof res === "string") {
            return;
          } else {
            originalRequest.headers[apiServiceHeadersConfig.secureSessionId] = res[0];
            return axiosInstance(originalRequest);
          }

          // Retry the original request
        } catch (keyExchangeError) {
          return handleResponseError(keyExchangeError);
        }
      }
      return handleResponseError(error);
    }

    handleResponseError(error);

    return Promise.reject(JSON.stringify(error.response?.data?.errors));
  }
);

export default axiosInstance;

export const updateAxiosConfig = (config: AxiosRequestConfig) => {
  axiosInstance.defaults.headers.common = {
    ...axiosInstance.defaults.headers.common,
    ...config.headers,
  };
};
